import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { format, parseISO, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import { todayMxDate } from '../lib/dateUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, 
  Search, 
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Loader2,
  X,
  Clock,
  User,
  Phone,
  MapPin,
  DollarSign,
  FileText,
  CalendarPlus,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const budgetStatusLabels = {
  pendiente: 'Pendiente',
  en_revision: 'En Revisión',
  enviado: 'Enviado',
  aprobado: 'Aprobado',
  rechazado: 'Rechazado',
  sin_respuesta: 'Sin Respuesta',
  agendado: 'Agendado'
};

const budgetStatusOptions = Object.entries(budgetStatusLabels).map(([value, label]) => ({ value, label }));

const serviceTypes = [
  'Sondeo de drenaje',
  'Plomería',
  'Electricidad',
  'Aire Acondicionado',
  'Detección de fugas',
  'Mantenimiento general',
  'Otro'
];

const contactMediums = [
  'Teléfono',
  'WhatsApp',
  'Correo',
  'Referido',
  'Redes sociales',
  'Otro'
];

// Status badge styles
const getStatusClass = (status) => {
  const classes = {
    pendiente: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    en_revision: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    enviado: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    aprobado: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    rechazado: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    sin_respuesta: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    agendado: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400'
  };
  return classes[status] || classes.pendiente;
};

export default function Budgets() {
  const { user } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    technician_id: 'all',
    date_from: '',
    date_to: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [convertData, setConvertData] = useState({ 
    service_date: '', 
    hora_inicio: '09:00',
    hora_fin: '10:00',
    technician_id: '',
    technician_name: '',
    monto_total: 0,
    anticipo: 0,
    comentarios: ''
  });

  function getEmptyForm() {
    return {
      client_name: '',
      client_id: '',
      phones: [''],
      address: '',
      address_reference: '',
      service_type: '',
      description: '',
      estimated_amount: 0,
      suggested_technician_id: 'unassigned',
      suggested_technician_name: '',
      tentative_date: '',
      internal_comments: '',
      status: 'pendiente',
      contact_medium: ''
    };
  }

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.technician_id && filters.technician_id !== 'all') params.append('technician_id', filters.technician_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const [budgetsRes, techsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/budgets?${params.toString()}`),
        axios.get(`${API}/technicians`),
        axios.get(`${API}/clients`)
      ]);

      setBudgets(budgetsRes.data);
      setTechnicians(techsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [search, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleQuickFilter = (filterType) => {
    const today = todayMxDate();
    let newFilters = { ...filters, status: 'all', date_from: '', date_to: '' };

    switch (filterType) {
      case 'today':
        newFilters.date_from = format(today, 'yyyy-MM-dd');
        newFilters.date_to = format(today, 'yyyy-MM-dd');
        break;
      case 'week':
        newFilters.date_from = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        newFilters.date_to = format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        break;
      case 'month':
        newFilters.date_from = format(startOfMonth(today), 'yyyy-MM-dd');
        newFilters.date_to = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      default:
        if (budgetStatusLabels[filterType]) {
          newFilters.status = filterType;
        }
    }

    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      technician_id: 'all',
      date_from: '',
      date_to: ''
    });
    setSearch('');
  };

  const openNewDialog = () => {
    setSelectedBudget(null);
    setFormData(getEmptyForm());
    setDialogOpen(true);
  };

  const openEditDialog = (budget) => {
    setSelectedBudget(budget);
    setFormData({
      ...budget,
      phones: budget.phones?.length ? budget.phones : [''],
      suggested_technician_id: budget.suggested_technician_id || 'unassigned'
    });
    setDialogOpen(true);
  };

  const openDetailDialog = async (budget) => {
    try {
      const res = await axios.get(`${API}/budgets/${budget.id}`);
      setSelectedBudget(res.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const handleClientSelect = (client) => {
    setFormData({
      ...formData,
      client_id: client.id,
      client_name: client.full_name,
      phones: client.phones?.length ? client.phones : [''],
      address: client.addresses?.[0]?.street || '',
      address_reference: client.addresses?.[0]?.reference || ''
    });
    setClientSearch('');
    setShowClientSuggestions(false);
  };

  const handleTechnicianChange = (techId) => {
    if (techId === 'unassigned') {
      setFormData({
        ...formData,
        suggested_technician_id: 'unassigned',
        suggested_technician_name: ''
      });
    } else {
      const tech = technicians.find(t => t.id === techId);
      setFormData({
        ...formData,
        suggested_technician_id: techId,
        suggested_technician_name: tech?.name || ''
      });
    }
  };

  const handlePhoneChange = (index, value) => {
    const newPhones = [...formData.phones];
    newPhones[index] = value;
    setFormData({ ...formData, phones: newPhones });
  };

  const addPhone = () => {
    setFormData({ ...formData, phones: [...formData.phones, ''] });
  };

  const removePhone = (index) => {
    const newPhones = formData.phones.filter((_, i) => i !== index);
    setFormData({ ...formData, phones: newPhones.length ? newPhones : [''] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_name || !formData.service_type) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        phones: formData.phones.filter(p => p.trim()),
        estimated_amount: parseFloat(formData.estimated_amount) || 0,
        suggested_technician_id: formData.suggested_technician_id === 'unassigned' ? '' : formData.suggested_technician_id
      };

      if (selectedBudget) {
        await axios.put(`${API}/budgets/${selectedBudget.id}`, data);
        toast.success('Presupuesto actualizado');
      } else {
        await axios.post(`${API}/budgets`, data);
        toast.success('Presupuesto creado');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBudget) return;
    try {
      await axios.delete(`${API}/budgets/${selectedBudget.id}`);
      toast.success('Presupuesto eliminado');
      setDeleteDialogOpen(false);
      setSelectedBudget(null);
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleStatusChange = async (budget, newStatus) => {
    try {
      await axios.put(`${API}/budgets/${budget.id}`, { status: newStatus });
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleConvertToService = async () => {
    if (!selectedBudget || !convertData.service_date) {
      toast.error('Selecciona una fecha para el servicio');
      return;
    }

    // Validaciones
    if (convertData.hora_fin <= convertData.hora_inicio) {
      toast.error('La hora de fin debe ser mayor a la hora de inicio');
      return;
    }

    const montoTotal = parseFloat(convertData.monto_total) || 0;
    const anticipo = parseFloat(convertData.anticipo) || 0;

    if (anticipo < 0) {
      toast.error('El anticipo no puede ser negativo');
      return;
    }

    if (anticipo > montoTotal) {
      toast.error('El anticipo no puede ser mayor al monto total');
      return;
    }

    setConverting(true);
    try {
      const res = await axios.post(
        `${API}/budgets/${selectedBudget.id}/convert-to-service`,
        {
          service_date: convertData.service_date,
          hora_inicio: convertData.hora_inicio,
          hora_fin: convertData.hora_fin,
          technician_id: convertData.technician_id || selectedBudget.suggested_technician_id || '',
          technician_name: convertData.technician_name || selectedBudget.suggested_technician_name || '',
          monto_total: montoTotal,
          anticipo: anticipo,
          comentarios: convertData.comentarios || ''
        }
      );
      toast.success(`Servicio creado: ${res.data.service_folio}`);
      if (res.data.pago?.restante > 0) {
        toast.info(`Restante por cobrar: $${res.data.pago.restante.toLocaleString()}`);
      }
      setConvertDialogOpen(false);
      setSelectedBudget(null);
      setConvertData({ 
        service_date: '', 
        hora_inicio: '09:00',
        hora_fin: '10:00',
        technician_id: '',
        technician_name: '',
        monto_total: 0,
        anticipo: 0,
        comentarios: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al convertir');
    } finally {
      setConverting(false);
    }
  };

  const openConvertDialog = (budget) => {
    setSelectedBudget(budget);
    setConvertData({
      service_date: budget.tentative_date || '',
      hora_inicio: '09:00',
      hora_fin: '10:00',
      technician_id: budget.suggested_technician_id || '',
      technician_name: budget.suggested_technician_name || '',
      monto_total: budget.estimated_amount || 0,
      anticipo: 0,
      comentarios: ''
    });
    setConvertDialogOpen(true);
  };

  const handleConvertTechnicianChange = (techId) => {
    if (techId === 'unassigned') {
      setConvertData({ ...convertData, technician_id: '', technician_name: '' });
    } else {
      const tech = technicians.find(t => t.id === techId);
      setConvertData({
        ...convertData,
        technician_id: techId,
        technician_name: tech?.name || ''
      });
    }
  };

  const filteredClients = clients.filter(c => 
    c.full_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.phones?.some(p => p.includes(clientSearch))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="budgets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Presupuestos</h2>
          <p className="text-muted-foreground text-sm">{budgets.length} presupuestos registrados</p>
        </div>
        <Button onClick={openNewDialog} data-testid="new-budget-button">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Presupuesto
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por folio, cliente, teléfono, dirección..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="budget-search-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant={showFilters ? 'secondary' : 'outline'} 
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filtros
              </Button>
              {(filters.status !== 'all' || filters.date_from) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-2" />
                  Limpiar
                </Button>
              )}
            </div>
          </div>

          {/* Quick Filters */}
          <div className="flex gap-2 mt-4 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => handleQuickFilter('today')}>Hoy</Button>
            <Button variant="outline" size="sm" onClick={() => handleQuickFilter('week')}>Esta Semana</Button>
            <Button variant="outline" size="sm" onClick={() => handleQuickFilter('month')}>Este Mes</Button>
            <span className="border-l mx-2" />
            <Button variant="outline" size="sm" onClick={() => handleQuickFilter('pendiente')}>
              <span className="w-2 h-2 rounded-full bg-amber-500 mr-2" />Pendientes
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleQuickFilter('enviado')}>
              <span className="w-2 h-2 rounded-full bg-purple-500 mr-2" />Enviados
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleQuickFilter('aprobado')}>
              <span className="w-2 h-2 rounded-full bg-green-500 mr-2" />Aprobados
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleQuickFilter('rechazado')}>
              <span className="w-2 h-2 rounded-full bg-red-500 mr-2" />Rechazados
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleQuickFilter('sin_respuesta')}>
              <span className="w-2 h-2 rounded-full bg-gray-500 mr-2" />Sin Respuesta
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleQuickFilter('agendado')}>
              <span className="w-2 h-2 rounded-full bg-teal-500 mr-2" />Agendados
            </Button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t">
              <div>
                <Label className="text-xs">Estado</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {budgetStatusOptions.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Técnico</Label>
                <Select
                  value={filters.technician_id}
                  onValueChange={(value) => setFilters({ ...filters, technician_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos los técnicos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los técnicos</SelectItem>
                    {technicians.filter(t => t.status === 'active' || t.is_active === true).map((tech) => (
                      <SelectItem key={tech.id} value={tech.id}>
                        {tech.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Fecha Desde</Label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Fecha Hasta</Label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Budgets Table */}
      <Card>
        <CardContent className="p-0">
          <ScrollArea className="h-[calc(100vh-420px)] min-h-[400px]">
            <Table className="service-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[110px]">Folio</TableHead>
                  <TableHead className="w-[90px]">Fecha</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="hidden lg:table-cell">Servicio</TableHead>
                  <TableHead className="hidden md:table-cell">Monto Est.</TableHead>
                  <TableHead className="hidden xl:table-cell">Folio Nota</TableHead>
                  <TableHead className="hidden xl:table-cell">Técnico Sugerido</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {budgets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No se encontraron presupuestos
                    </TableCell>
                  </TableRow>
                ) : (
                  budgets.map((budget) => (
                    <TableRow key={budget.id} data-testid={`budget-row-${budget.id}`}>
                      <TableCell className="font-mono text-xs">{budget.folio}</TableCell>
                      <TableCell className="text-sm">
                        {budget.created_at ? format(parseISO(budget.created_at), 'dd/MM/yy') : '-'}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm truncate max-w-[150px]">{budget.client_name}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                            {budget.phones?.[0]}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <p className="text-sm truncate max-w-[150px]">{budget.service_type}</p>
                      </TableCell>
                      <TableCell className="hidden md:table-cell font-medium">
                        ${budget.estimated_amount?.toLocaleString() || 0}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell">
                        <span className="font-mono text-xs">{budget.folio_nota || '-'}</span>
                      </TableCell>
                      <TableCell className="hidden xl:table-cell">
                        <p className="text-sm truncate max-w-[120px]">{budget.suggested_technician_name || '-'}</p>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-auto p-0">
                              <Badge className={`${getStatusClass(budget.status)} cursor-pointer`}>
                                {budgetStatusLabels[budget.status]}
                              </Badge>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            {budgetStatusOptions.filter(s => s.value !== 'agendado').map(s => (
                              <DropdownMenuItem
                                key={s.value}
                                onClick={() => handleStatusChange(budget, s.value)}
                              >
                                <span className={`w-2 h-2 rounded-full mr-2 ${getStatusClass(s.value).split(' ')[0]}`} />
                                {s.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDetailDialog(budget)}>
                              <Eye className="h-4 w-4 mr-2" />
                              Ver Detalle
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditDialog(budget)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            {budget.status !== 'agendado' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => openConvertDialog(budget)}
                                  className="text-green-600"
                                >
                                  <CalendarPlus className="h-4 w-4 mr-2" />
                                  Agendar Servicio
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedBudget(budget);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Budget Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedBudget ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
            </DialogTitle>
            <DialogDescription>
              {selectedBudget ? 'Modifica los datos del presupuesto' : 'Completa los datos para crear un nuevo presupuesto'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Client Search */}
            <div className="space-y-2">
              <Label>Buscar Cliente Existente</Label>
              <div className="relative">
                <Input
                  placeholder="Buscar por nombre o teléfono..."
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setShowClientSuggestions(e.target.value.length > 0);
                  }}
                  onFocus={() => setShowClientSuggestions(clientSearch.length > 0)}
                />
                {showClientSuggestions && filteredClients.length > 0 && (
                  <Card className="absolute z-10 w-full mt-1 max-h-48 overflow-auto">
                    {filteredClients.slice(0, 5).map(client => (
                      <div
                        key={client.id}
                        className="p-2 hover:bg-muted cursor-pointer"
                        onClick={() => handleClientSelect(client)}
                      >
                        <p className="font-medium text-sm">{client.full_name}</p>
                        <p className="text-xs text-muted-foreground">{client.phones?.join(', ')}</p>
                      </div>
                    ))}
                  </Card>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Nombre del Cliente *</Label>
              <Input
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                placeholder="Nombre completo"
                required
                data-testid="budget-client-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Teléfonos</Label>
              {formData.phones.map((phone, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={phone}
                    onChange={(e) => handlePhoneChange(index, e.target.value)}
                    placeholder="Número de teléfono"
                  />
                  {formData.phones.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removePhone(index)}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addPhone}>
                <Plus className="h-4 w-4 mr-2" />
                Agregar Teléfono
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Domicilio</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Dirección completa"
              />
            </div>

            <div className="space-y-2">
              <Label>Referencias del Domicilio</Label>
              <Input
                value={formData.address_reference}
                onChange={(e) => setFormData({ ...formData, address_reference: e.target.value })}
                placeholder="Ej: Casa azul, frente al parque"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Servicio *</Label>
                <Select 
                  value={formData.service_type} 
                  onValueChange={(v) => setFormData({ ...formData, service_type: v })}
                >
                  <SelectTrigger data-testid="budget-service-type-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Medio de Contacto</Label>
                <Select 
                  value={formData.contact_medium} 
                  onValueChange={(v) => setFormData({ ...formData, contact_medium: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {contactMediums.map(medium => (
                      <SelectItem key={medium} value={medium}>{medium}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descripción del Trabajo</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe el problema o trabajo solicitado"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monto Estimado</Label>
                <Input
                  type="number"
                  value={formData.estimated_amount}
                  onChange={(e) => setFormData({ ...formData, estimated_amount: e.target.value })}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  data-testid="budget-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha Tentativa</Label>
                <Input
                  type="date"
                  value={formData.tentative_date}
                  onChange={(e) => setFormData({ ...formData, tentative_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Técnico Sugerido</Label>
              <Select 
                value={formData.suggested_technician_id} 
                onValueChange={handleTechnicianChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Sin asignar</SelectItem>
                  {technicians.filter(t => t.is_active).map(tech => (
                    <SelectItem key={tech.id} value={tech.id}>{tech.name} - {tech.specialty}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Estado</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {budgetStatusOptions.filter(s => s.value !== 'agendado').map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Comentarios Internos</Label>
              <Textarea
                value={formData.internal_comments}
                onChange={(e) => setFormData({ ...formData, internal_comments: e.target.value })}
                placeholder="Notas internas"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-budget-button">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  selectedBudget ? 'Actualizar' : 'Crear Presupuesto'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Budget Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Detalle del Presupuesto
            </DialogTitle>
          </DialogHeader>
          {selectedBudget && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Folio</p>
                  <p className="font-mono font-bold">{selectedBudget.folio}</p>
                </div>
                <Badge className={getStatusClass(selectedBudget.status)}>
                  {budgetStatusLabels[selectedBudget.status]}
                </Badge>
              </div>

              {selectedBudget.status === 'agendado' && selectedBudget.converted_to_service_id && (
                <div className="p-3 bg-teal-50 dark:bg-teal-950/30 rounded-lg flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <div>
                    <p className="text-sm font-medium text-teal-800 dark:text-teal-400">
                      Convertido a Servicio
                    </p>
                    <p className="text-xs text-teal-700 dark:text-teal-500">
                      {selectedBudget.converted_at && format(parseISO(selectedBudget.converted_at), "dd/MM/yyyy 'a las' HH:mm")}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Creado</p>
                    <p className="text-sm font-medium">
                      {selectedBudget.created_at ? format(parseISO(selectedBudget.created_at), 'PPP', { locale: es }) : '-'}
                    </p>
                  </div>
                </div>
                {selectedBudget.tentative_date && (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Fecha Tentativa</p>
                      <p className="text-sm font-medium">
                        {format(parseISO(selectedBudget.tentative_date), 'PPP', { locale: es })}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
                <div className="flex items-start gap-2">
                  <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Cliente</p>
                    <p className="font-medium">{selectedBudget.client_name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Teléfonos</p>
                    <p className="text-sm">{selectedBudget.phones?.join(', ') || '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Domicilio</p>
                    <p className="text-sm">{selectedBudget.address || '-'}</p>
                    {selectedBudget.address_reference && (
                      <p className="text-xs text-muted-foreground mt-1">Ref: {selectedBudget.address_reference}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Tipo de Servicio</p>
                  <p className="font-medium">{selectedBudget.service_type}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Técnico Sugerido</p>
                  <p className="font-medium">{selectedBudget.suggested_technician_name || 'Sin asignar'}</p>
                </div>
              </div>

              {selectedBudget.description && (
                <div>
                  <p className="text-xs text-muted-foreground">Descripción</p>
                  <p className="text-sm mt-1">{selectedBudget.description}</p>
                </div>
              )}

              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Monto Estimado</p>
                  <p className="text-lg font-bold">${selectedBudget.estimated_amount?.toLocaleString() || 0}</p>
                </div>
              </div>

              {/* Información del Servicio Origen */}
              {selectedBudget.servicio_origen_id && (
                <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <p className="text-xs font-medium text-blue-800 dark:text-blue-400">Generado desde Servicio</p>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    {selectedBudget.folio_nota && (
                      <div>
                        <p className="text-xs text-muted-foreground">Folio Nota</p>
                        <p className="font-mono font-medium">{selectedBudget.folio_nota}</p>
                      </div>
                    )}
                    {selectedBudget.monto_cobrado > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground">Monto Cobrado</p>
                        <p className="font-medium text-green-600">${selectedBudget.monto_cobrado?.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedBudget.internal_comments && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                  <p className="text-xs font-medium text-amber-800 dark:text-amber-400">Comentarios Internos</p>
                  <p className="text-sm mt-1">{selectedBudget.internal_comments}</p>
                </div>
              )}

              <div className="pt-4 border-t">
                <p className="text-xs text-muted-foreground mb-2">Registro</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Creado por</p>
                    <p>{selectedBudget.created_by}</p>
                  </div>
                  {selectedBudget.updated_by && (
                    <div>
                      <p className="text-muted-foreground">Última modificación</p>
                      <p>{selectedBudget.updated_by}</p>
                    </div>
                  )}
                </div>
              </div>

              {selectedBudget.history?.length > 0 && (
                <div className="pt-4 border-t">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Historial de Cambios</p>
                  <ScrollArea className="h-40">
                    <div className="space-y-2">
                      {selectedBudget.history.map((log, idx) => (
                        <div key={idx} className="text-xs p-2 bg-muted/50 rounded">
                          <div className="flex justify-between">
                            <span className="font-medium">{log.action}</span>
                            <span className="text-muted-foreground">
                              {format(parseISO(log.timestamp), 'dd/MM/yy HH:mm')}
                            </span>
                          </div>
                          <p className="text-muted-foreground mt-1">Por: {log.performed_by}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {selectedBudget.status !== 'agendado' && (
                <div className="pt-4 border-t">
                  <Button 
                    onClick={() => {
                      setDetailDialogOpen(false);
                      openConvertDialog(selectedBudget);
                    }}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <CalendarPlus className="h-4 w-4 mr-2" />
                    Agendar Servicio
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Convert to Service Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-green-600" />
              Agendar Servicio
            </DialogTitle>
            <DialogDescription>
              Convierte este presupuesto en un servicio agendado
            </DialogDescription>
          </DialogHeader>
          {selectedBudget && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex justify-between">
                  <div>
                    <p className="text-sm font-medium">{selectedBudget.client_name}</p>
                    <p className="text-xs text-muted-foreground">{selectedBudget.service_type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Monto estimado</p>
                    <p className="font-bold">${selectedBudget.estimated_amount?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </div>

              {/* Fecha del servicio */}
              <div className="space-y-2">
                <Label>Fecha del Servicio *</Label>
                <Input
                  type="date"
                  value={convertData.service_date}
                  onChange={(e) => setConvertData({ ...convertData, service_date: e.target.value })}
                  required
                  data-testid="convert-fecha-input"
                />
              </div>

              {/* Rango de horario */}
              <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-400 mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Rango de Horario
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Hora Inicio *</Label>
                    <Input
                      type="time"
                      value={convertData.hora_inicio}
                      onChange={(e) => setConvertData({ ...convertData, hora_inicio: e.target.value })}
                      data-testid="convert-hora-inicio-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Hora Fin *</Label>
                    <Input
                      type="time"
                      value={convertData.hora_fin}
                      onChange={(e) => setConvertData({ ...convertData, hora_fin: e.target.value })}
                      data-testid="convert-hora-fin-input"
                    />
                  </div>
                </div>
                {convertData.hora_fin <= convertData.hora_inicio && convertData.hora_fin && (
                  <p className="text-xs text-red-600 flex items-center gap-1 mt-2">
                    <AlertCircle className="h-3 w-3" />
                    La hora de fin debe ser mayor a la hora de inicio
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Horario: <span className="font-medium">{convertData.hora_inicio} a {convertData.hora_fin}</span>
                </p>
              </div>

              <div className="space-y-2">
                <Label>Técnico Asignado</Label>
                <Select
                  value={convertData.technician_id || 'unassigned'}
                  onValueChange={handleConvertTechnicianChange}
                >
                  <SelectTrigger data-testid="convert-tecnico-select">
                    <SelectValue placeholder="Seleccionar técnico" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Sin asignar</SelectItem>
                    {technicians.filter(t => t.status === 'active' || t.is_active === true).map((tech) => (
                      <SelectItem key={tech.id} value={tech.id}>
                        {tech.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Información de Pago con resumen visual */}
              <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800 space-y-4">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-400 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Información de Pago
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Monto Total *</Label>
                    <Input
                      type="number"
                      value={convertData.monto_total}
                      onChange={(e) => {
                        const monto = parseFloat(e.target.value) || 0;
                        setConvertData({ 
                          ...convertData, 
                          monto_total: monto
                        });
                      }}
                      min="0"
                      step="0.01"
                      data-testid="convert-monto-total-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Anticipo</Label>
                    <Input
                      type="number"
                      value={convertData.anticipo}
                      onChange={(e) => {
                        const anticipo = parseFloat(e.target.value) || 0;
                        setConvertData({ 
                          ...convertData, 
                          anticipo: anticipo
                        });
                      }}
                      min="0"
                      step="0.01"
                      max={convertData.monto_total}
                      data-testid="convert-anticipo-input"
                    />
                  </div>
                </div>

                {/* Resumen visual de pagos */}
                <div className="p-3 bg-white dark:bg-zinc-900 rounded-lg border">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Resumen del Acuerdo:</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Monto total:</span>
                      <span className="font-medium">${(parseFloat(convertData.monto_total) || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Anticipo:</span>
                      <span className="text-green-600 font-medium">- ${(parseFloat(convertData.anticipo) || 0).toLocaleString()}</span>
                    </div>
                    <div className="border-t pt-1 flex justify-between font-semibold text-base">
                      <span className="text-orange-600">Restante:</span>
                      <span className={`${((parseFloat(convertData.monto_total) || 0) - (parseFloat(convertData.anticipo) || 0)) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        ${Math.max(0, (parseFloat(convertData.monto_total) || 0) - (parseFloat(convertData.anticipo) || 0)).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Validaciones */}
                {convertData.anticipo < 0 && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    El anticipo no puede ser negativo
                  </p>
                )}
                {convertData.anticipo > convertData.monto_total && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    El anticipo no puede ser mayor al monto total
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Comentarios</Label>
                <Textarea
                  value={convertData.comentarios}
                  onChange={(e) => setConvertData({ ...convertData, comentarios: e.target.value })}
                  placeholder="Observaciones del cliente, detalles del trabajo, indicaciones especiales..."
                  rows={3}
                  data-testid="convert-comentarios-textarea"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setConvertDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleConvertToService} 
                  disabled={converting || convertData.anticipo > convertData.monto_total}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {converting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Convirtiendo...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Confirmar y Agendar
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Presupuesto</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar este presupuesto? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
